import { Link } from "react-router-dom";
import { IMG, PURE_SOULS_URL } from "@/lib/site";
import SectionHeader from "@/components/site/SectionHeader";
import Seo from "@/components/site/Seo";
import { PAGE_SEO, breadcrumbLd } from "@/lib/seo";

export default function WhatIsWaterCremation() {
  return (
    <div data-testid="what-is-page">
      <Seo
        {...PAGE_SEO.whatIs}
        jsonLd={breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "What Is Water Cremation", path: "/what-is-water-cremation" },
        ])}
      />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={IMG.texWater} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#F9F9F6]/85" />
        </div>
        <div className="container-editorial pt-20 pb-16">
          <div className="overline mb-5">Pillar Page</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] text-forest-900 max-w-4xl">
            What is water cremation?
          </h1>
          <p className="mt-6 max-w-2xl text-lg sm:text-xl text-forest-700 leading-[1.65]">
            A deep, calm exploration of the gentle water based process, also
            known as aqua cremation or alkaline hydrolysis, and why families across
            Australia are increasingly choosing it.
          </p>
        </div>
      </section>

      {/* Reading column */}
      <section className="container-reading pb-20">
        <div className="editorial-prose">
          <p>
            Water cremation is an environmentally conscious form of pet
            aftercare that uses warm water, gentle circulation, and an alkaline
            solution to accelerate the natural process of returning the body to
            its basic organic elements. The process is also widely known as{" "}
            <em>aqua cremation</em> or <em>alkaline hydrolysis</em>.
          </p>

          <h2>An overview of the process</h2>
          <p>
            Inside a stainless steel vessel, the pet&rsquo;s body is gently submerged
            in warm water mixed with a small, precise amount of alkali. Over the
            course of several hours, the combination of warmth, gentle water
            movement, and chemistry breaks down soft tissue, leaving behind
            clean mineral remains, primarily calcium and phosphorus.
          </p>
          <p>
            There are no flames, no smoke, and no airborne emissions. The water
            used is treated and safely returned to the local water system in
            compliance with strict environmental and health regulations.
          </p>

          <blockquote>
            Water cremation mirrors a natural cycle: gently, quietly, and at an
            accelerated pace.
          </blockquote>

          <h2>The remains returned</h2>
          <p>
            What families receive after water cremation is a soft, fine,
            light-coloured powder. It is mineral, clean, and often described as
            feeling more like soft sand than ash. The texture and colour reflect
            the absence of soot or scorching, qualities unique to flame
            cremation.
          </p>

          <h2>Why families consider water cremation</h2>
          <ul>
            <li>A gentler experience that aligns with how they wish to remember a pet</li>
            <li>Significantly lower carbon footprint compared to flame cremation</li>
            <li>Individual care and identification throughout the entire process</li>
            <li>Returned remains suitable for memorial keepsakes or scattering</li>
            <li>A modern approach that reflects environmentally conscious values</li>
          </ul>

          <h2>Is it widely available in Australia?</h2>
          <p>
            Availability is still developing across Australia, but it continues
            to grow as more families seek sustainable, gentler alternatives.
            Queensland currently leads in availability, with premium providers
            such as Pure Souls Pet Aqua Cremation in Townsville offering
            considered, individual care to families across the state.
          </p>
        </div>

        <div className="divider-leaf">
          <span className="text-xs uppercase tracking-[0.22em]">A Gentle Pause</span>
        </div>

        <div className="bg-sage-50 border border-sage-100 rounded-2xl p-8 sm:p-10">
          <div className="overline mb-3">Featured Provider</div>
          <h3 className="font-serif text-3xl text-forest-900 leading-tight mb-3">
            Pure Souls Pet Aqua Cremation
          </h3>
          <p className="text-forest-700 leading-relaxed mb-5">
            Premium pet water cremation services in Townsville, Queensland, focused on compassionate care, sustainability, and meaningful
            memorial experiences for families.
          </p>
          <a
            href={PURE_SOULS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            data-testid="what-is-pure-souls-cta"
          >
            Visit Pure Souls →
          </a>
        </div>

        <div className="mt-16 flex flex-wrap gap-4">
          <Link to="/the-process" className="btn-secondary">Read about the process</Link>
          <Link to="/benefits" className="btn-secondary">Explore the benefits</Link>
        </div>
      </section>
    </div>
  );
}
